import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-'
  
  const d = typeof date === 'string' ? new Date(date) : date
  
  // Verificar se a data é válida
  if (isNaN(d.getTime())) {
    return '-'
  }
  
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function formatCNPJ(cnpj: string): string {
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

export function formatCPF(cpf: string): string {
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
  }
  return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')
}

/**
 * Formata um timestamp em tempo relativo (ex: "há 5 minutos", "há 2 horas")
 */
export function formatRelativeTime(timestamp: number | null | undefined): string {
  if (!timestamp) return 'Nunca'

  const now = Date.now()
  const diff = now - timestamp // diferença em milissegundos
  
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) {
    return 'Agora'
  } else if (minutes < 60) {
    return `há ${minutes} min`
  } else if (hours < 24) {
    return `há ${hours}h`
  } else if (days === 1) {
    return 'Ontem'
  } else {
    return `há ${days} dias`
  }
}

/**
 * Converte cor hexadecimal para versão mais clara/escura (tom pastel)
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const sanitized = hex.replace('#', '')
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) return null

  const r = parseInt(sanitized.substring(0, 2), 16) / 255
  const g = parseInt(sanitized.substring(2, 4), 16) / 255
  const b = parseInt(sanitized.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

export interface IndustriaColorClasses {
  border: string
  bg: string
  bgLight: string
  text: string
  textDark: string
  ring: string
  cor: string
}

/**
 * Gera classes de estilo e cor para cards de indústria.
 * Aceita nomes de cor (blue, red...) ou hexadecimal (#RRGGBB).
 */
export function getIndustriaColorClasses(cor?: string | null): IndustriaColorClasses {
  const namedMap: Record<string, IndustriaColorClasses> = {
    blue: { border: 'border-blue-300 dark:border-blue-700', bg: 'bg-blue-100 dark:bg-blue-900/50', bgLight: 'bg-blue-50/80 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-300', textDark: 'text-blue-900 dark:text-blue-100', ring: 'ring-blue-200 dark:ring-blue-800', cor: '#3B82F6' },
    purple: { border: 'border-purple-300 dark:border-purple-700', bg: 'bg-purple-100 dark:bg-purple-900/50', bgLight: 'bg-purple-50/80 dark:bg-purple-950/30', text: 'text-purple-700 dark:text-purple-300', textDark: 'text-purple-900 dark:text-purple-100', ring: 'ring-purple-200 dark:ring-purple-800', cor: '#9333EA' },
    green: { border: 'border-green-300 dark:border-green-700', bg: 'bg-green-100 dark:bg-green-900/50', bgLight: 'bg-green-50/80 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-300', textDark: 'text-green-900 dark:text-green-100', ring: 'ring-green-200 dark:ring-green-800', cor: '#22C55E' },
    orange: { border: 'border-orange-300 dark:border-orange-700', bg: 'bg-orange-100 dark:bg-orange-900/50', bgLight: 'bg-orange-50/80 dark:bg-orange-950/30', text: 'text-orange-700 dark:text-orange-300', textDark: 'text-orange-900 dark:text-orange-100', ring: 'ring-orange-200 dark:ring-orange-800', cor: '#F97316' },
    pink: { border: 'border-pink-300 dark:border-pink-700', bg: 'bg-pink-100 dark:bg-pink-900/50', bgLight: 'bg-pink-50/80 dark:bg-pink-950/30', text: 'text-pink-700 dark:text-pink-300', textDark: 'text-pink-900 dark:text-pink-100', ring: 'ring-pink-200 dark:ring-pink-800', cor: '#EC4899' },
    indigo: { border: 'border-indigo-300 dark:border-indigo-700', bg: 'bg-indigo-100 dark:bg-indigo-900/50', bgLight: 'bg-indigo-50/80 dark:bg-indigo-950/30', text: 'text-indigo-700 dark:text-indigo-300', textDark: 'text-indigo-900 dark:text-indigo-100', ring: 'ring-indigo-200 dark:ring-indigo-800', cor: '#6366F1' },
    red: { border: 'border-red-300 dark:border-red-700', bg: 'bg-red-100 dark:bg-red-900/50', bgLight: 'bg-red-50/80 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-300', textDark: 'text-red-900 dark:text-red-100', ring: 'ring-red-200 dark:ring-red-800', cor: '#EF4444' },
    yellow: { border: 'border-yellow-300 dark:border-yellow-700', bg: 'bg-yellow-100 dark:bg-yellow-900/50', bgLight: 'bg-yellow-50/80 dark:bg-yellow-950/30', text: 'text-yellow-700 dark:text-yellow-300', textDark: 'text-yellow-900 dark:text-yellow-100', ring: 'ring-yellow-200 dark:ring-yellow-800', cor: '#EAB308' },
    cyan: { border: 'border-cyan-300 dark:border-cyan-700', bg: 'bg-cyan-100 dark:bg-cyan-900/50', bgLight: 'bg-cyan-50/80 dark:bg-cyan-950/30', text: 'text-cyan-700 dark:text-cyan-300', textDark: 'text-cyan-900 dark:text-cyan-100', ring: 'ring-cyan-200 dark:ring-cyan-800', cor: '#06B6D4' },
    emerald: { border: 'border-emerald-300 dark:border-emerald-700', bg: 'bg-emerald-100 dark:bg-emerald-900/50', bgLight: 'bg-emerald-50/80 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-300', textDark: 'text-emerald-900 dark:text-emerald-100', ring: 'ring-emerald-200 dark:ring-emerald-800', cor: '#10B981' },
    teal: { border: 'border-teal-300 dark:border-teal-700', bg: 'bg-teal-100 dark:bg-teal-900/50', bgLight: 'bg-teal-50/80 dark:bg-teal-950/30', text: 'text-teal-700 dark:text-teal-300', textDark: 'text-teal-900 dark:text-teal-100', ring: 'ring-teal-200 dark:ring-teal-800', cor: '#14B8A6' },
    lime: { border: 'border-lime-300 dark:border-lime-700', bg: 'bg-lime-100 dark:bg-lime-900/50', bgLight: 'bg-lime-50/80 dark:bg-lime-950/30', text: 'text-lime-700 dark:text-lime-300', textDark: 'text-lime-900 dark:text-lime-100', ring: 'ring-lime-200 dark:ring-lime-800', cor: '#84CC16' },
    amber: { border: 'border-amber-300 dark:border-amber-700', bg: 'bg-amber-100 dark:bg-amber-900/50', bgLight: 'bg-amber-50/80 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-300', textDark: 'text-amber-900 dark:text-amber-100', ring: 'ring-amber-200 dark:ring-amber-800', cor: '#F59E0B' },
  }

  const normalized = cor?.toLowerCase().trim() || 'blue'
  if (namedMap[normalized]) return namedMap[normalized]

  const hsl = hexToHsl(normalized)
  if (hsl) {
    const { h, s, l } = hsl
    return {
      border: '',
      bg: '',
      bgLight: '',
      text: '',
      textDark: '',
      ring: '',
      cor: normalized,
      _style: {
        borderColor: `hsl(${h} ${Math.max(20, s * 0.7)}% ${l > 50 ? '70%' : '40%'})`,
        backgroundColor: `hsl(${h} ${Math.max(20, s * 0.5)}% ${l > 50 ? '95%' : '20%'})`,
        backgroundColorLight: `hsl(${h} ${Math.max(15, s * 0.4)}% ${l > 50 ? '97%' : '15%'})`,
        color: `hsl(${h} ${Math.max(40, s)}% ${l > 50 ? '30%' : '80%'})`,
        colorDark: `hsl(${h} ${Math.max(40, s)}% ${l > 50 ? '20%' : '90%'})`,
        ringColor: `hsl(${h} ${Math.max(20, s * 0.6)}% ${l > 50 ? '80%' : '30%'})`,
      },
    } as IndustriaColorClasses & { _style: Record<string, string> }
  }

  return namedMap.blue
}

/**
 * Retorna estilos inline para uma cor hexadecimal customizada,
 * ou undefined se for uma cor nomeada do Tailwind.
 */
export interface IndustriaInlineStyles {
  cor: string
  borderColor: string
  backgroundColor: string
  backgroundColorLight: string
  color: string
  colorDark: string
  ringColor: string
}

export function getIndustriaInlineStyles(cor?: string | null): IndustriaInlineStyles | undefined {
  const classes = getIndustriaColorClasses(cor)
  const style = (classes as any)._style as Omit<IndustriaInlineStyles, 'cor'> | undefined
  if (!style) return undefined
  return { ...style, cor: classes.cor }
}
